/**
 * CrossLinkChips — Three icon chips visualising which of the Gutachten /
 * Mietwagen / Werkstatt akten are linked to a Forderung (Issue #332).
 *
 *   ● filled chip  →  verknüpft; click opens the target akte (deep-link, new
 *                     tab). Hover shows Akten-Nr.
 *   ○ hollow chip  →  not linked; tooltip explains what's missing.
 *
 * Pure presentation: the caller derives the chip state via
 * `deriveCrossLinks(...)` from `lib/cross-link-chips.ts` and passes the result
 * in. Keeping the data logic outside the component lets us test the derivation
 * without jsdom and keeps render cost minimal (important for the 1000-row
 * performance target).
 */

import { FileText, Car, Wrench, type LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CrossLinkChip, CrossLinkChipState } from '@/lib/cross-link-chips';

type ChipKey = 'G' | 'M' | 'W';

const META: Record<ChipKey, { label: string; missingLabel: string; Icon: LucideIcon; filledClass: string }> = {
  G: {
    label: 'Gutachten-Akte',
    missingLabel: 'Kein Gutachten verknüpft',
    Icon: FileText,
    filledClass: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
  },
  M: {
    label: 'Mietwagen-Akte',
    missingLabel: 'Keine Mietwagen-Akte verknüpft',
    Icon: Car,
    filledClass: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
  },
  W: {
    label: 'Werkstatt-Akte',
    missingLabel: 'Keine Werkstatt-Akte verknüpft',
    Icon: Wrench,
    filledClass: 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
  },
};

const HOLLOW_CLASS =
  'bg-transparent text-muted-foreground/60 border-dashed border-muted-foreground/30';

interface CrossLinkChipsProps {
  state: CrossLinkChipState;
  /** Use a smaller footprint inside dense rows. Default: false. */
  compact?: boolean;
  className?: string;
}

export function CrossLinkChips({ state, compact = false, className }: CrossLinkChipsProps) {
  return (
    <div
      className={cn('inline-flex items-center gap-1', className)}
      role="group"
      aria-label="Verknüpfungen"
      data-testid="cross-link-chips"
    >
      {(['G', 'M', 'W'] as ChipKey[]).map((key) => (
        <Chip key={key} chipKey={key} value={state[key]} compact={compact} />
      ))}
    </div>
  );
}

function Chip({
  chipKey,
  value,
  compact,
}: {
  chipKey: ChipKey;
  value: CrossLinkChip | null;
  compact: boolean;
}) {
  const { Icon, label, missingLabel, filledClass } = META[chipKey];
  const filled = value !== null;
  const size = compact ? 'w-6 h-6' : 'w-7 h-7';
  const iconSize = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';

  const tooltipText = filled ? `${label}: ${value.akten_nr}` : missingLabel;
  const classes = cn(
    'inline-flex items-center justify-center rounded-full border transition-colors',
    size,
    filled ? filledClass : HOLLOW_CLASS,
  );

  const body = <Icon className={iconSize} aria-hidden="true" />;

  if (filled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={value.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={cn(classes, 'cursor-pointer')}
            aria-label={tooltipText}
            data-testid={`cross-link-chip-${chipKey}-filled`}
          >
            {body}
          </a>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(classes, 'cursor-default')}
          aria-label={tooltipText}
          data-testid={`cross-link-chip-${chipKey}-empty`}
        >
          {body}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}
